'use client';

import { usePathname } from 'next/navigation';
import { resolveAuthenticatedIndividualPageShell } from '@/lib/authenticated-page-shell-routes';

interface PageTransitionProps {
  children: React.ReactNode;
}

// Route groups whose primary tabs share one persistent shell instance
// (see src/app/individual-dashboard/layout.tsx). Keying the transition by
// exact pathname would force React to tear down and remount that shell
// (sidebar/topbar/bottom nav, and every effect inside it) on every tab tap,
// since this component wraps the app above the Next.js layout router and a
// key change here discards the whole subtree regardless of Next's own
// layout persistence. Routes in the same group keep one stable key instead;
// everything else keeps its previous per-pathname behavior.
export function transitionKey(pathname: string): string {
  const inStableShell = resolveAuthenticatedIndividualPageShell(pathname) !== null;
  // All stable-shell routes share one canonical key (not each route's own
  // prefix) so crossing between them — e.g. individual-dashboard to
  // messages — also skips the remount, not just navigation within one group.
  return inStableShell ? 'member-shell' : pathname;
}

/**
 * Light route transition. Must render `children` directly — do not cache
 * `children` in React state: Next.js layout slots rely on the live tree and
 * stale copies break the layout router ("invariant expected layout router to be mounted").
 */
export default function PageTransition({ children }: PageTransitionProps) {
  const pathname = usePathname();

  return (
    <div key={transitionKey(pathname)} className="motion-safe:animate-fade-in-shell">
      {children}
    </div>
  );
}
